import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { ImpactBadge, WhyThisMatters, getPlatformIcon } from '../components/Shared.jsx';

export function SocialAudit({ data }) {
  const { platformStatus: rawPlatforms = [], missingPlatforms: rawMissing = [], recommendations = [], summary } = data;
  // Filter "Website" from platform list — not a social platform, no actionable link
  const platformStatus = rawPlatforms.filter((p) => !/^website$/i.test(p.platform));
  // Filter out Apple Podcasts and Spotify — the podcast is already on these by definition
  const missingPlatforms = rawMissing.filter((mp) => !/^(apple podcasts?|spotify)$/i.test(mp.platform));
  const statusColor = (s) => s === 'active' ? T.success : s === 'inactive' ? T.warning : T.error;

  return (
    <Box>
      {platformStatus.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Platform Status</Typography>
          {platformStatus.map((p, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.75, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Box sx={{ color: T.accent, display: 'flex', alignItems: 'center' }}>{getPlatformIcon(p.platform)}</Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text, minWidth: 90 }}>{p.platform}</Typography>
                {p.handle && p.handle !== 'unknown' && (
                  p.profileUrl && p.profileUrl !== 'unknown'
                    ? <Typography component="a" href={p.profileUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 13, color: T.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{p.handle}</Typography>
                    : <Typography sx={{ fontSize: 13, color: T.accent }}>{p.handle}</Typography>
                )}
                {/* Fix 33: Status badges hidden — AI returns unreliable status (all ABANDONED). TODO 1.5: re-enable with real activity data */}
                {/* <Box sx={{ px: 1, py: 0.25, background: p.status === 'active' ? 'rgba(34,197,94,0.15)' : p.status === 'inactive' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)', color: statusColor(p.status), borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>{p.status}</Box> */}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {p.followers > 0 && <Typography sx={{ fontSize: 11, color: T.textSecondary }}>{p.followers.toLocaleString()} followers</Typography>}
                {/* Fix 33: Cadence + lastPost hidden — unreliable AI data. TODO 1.5: re-enable with real metrics */}
                {/* <Typography sx={{ fontSize: 11, color: T.textSecondary }}>{p.cadence}</Typography> */}
                {/* <Typography sx={{ fontSize: 11, color: T.textSecondary }}>Last: {p.lastPostDaysAgo}d ago</Typography> */}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {missingPlatforms.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.warning, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Missing Platforms</Typography>
          {missingPlatforms.map((mp, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(234,179,8,0.07)', border: `1px solid rgba(234,179,8,0.25)`, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                <Box sx={{ color: T.accent, display: 'flex', alignItems: 'center' }}>{getPlatformIcon(mp.platform)}</Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text }}>{mp.platform}</Typography>
                <ImpactBadge value={mp.priority} />
              </Box>
              <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{mp.recommendation}</Typography>
              <Typography sx={{ fontSize: 11, color: T.textSecondary, mt: 0.25, fontStyle: 'italic' }}>{mp.rationale}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {recommendations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Recommendations</Typography>
          {recommendations.map((rec, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1, display: 'flex', gap: 1 }}>
              <ImpactBadge value={rec.impact} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{rec.recommendation}</Typography>
                <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.25 }}>{rec.implementation}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {summary && (
        <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, color: T.textSecondary }}>{summary}</Typography>
        </Box>
      )}

      <WhyThisMatters text="Strong social presence amplifies reach, builds community, and signals authority." citations={data.expertCitations} />
    </Box>
  );
}
